import _ from "lodash";
import { DateTime } from "luxon";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
	Accordion,
	Button,
	Checkbox,
	type CheckboxProps,
	Dropdown,
	type DropdownProps,
	Icon,
	Popup,
} from "semantic-ui-react";
import { AnalysisType, TrialAnalysisDataOptions } from "../../../apis/types/trialTypes";
import { useTrialAnalysis, useTrialAnalysisDispatch } from "../../Content/Trials/TrialAnalysis/TrialAnalysisContext";
import {
	getGroundDataAssessments,
	getGrowthCurveAssessments,
	getOrderedTimeSeriesPresets,
} from "../../Content/Trials/TrialAnalysis/utils";
import styles from "./styles.module.css";
import anovaPdf from "../../../assets/aerialFARMS_v4.2_Update_-_Repeated_Measures_vs_Per_Flight_Date_Aug_25.pdf";

const TIME_SERIES_CATEGORIES = ["Aerial Time Series"];
const GROUND_DATA_CATEGORIES = ["Manual Ratings", "Ground Metrics"];
const GROWTH_CURVE_CATEGORIES = ["Phenotypes", "Growth Parameters"];

enum Category {
	TimeSeries = 1,
	GroundData,
	GrowthCurve,
}

const CATEGORIES_WITH_DATES = [Category.TimeSeries, Category.GroundData];

const removeExclusionsInfo =
	"When toggled to the 'on' position any regions the user has previously marked as excluded will be discarded from analyses";
const anovaTypeInfo = (
	<div>
		<p>Selection Determines which ANOVA results are displayed.</p>
		<p>
			<b>Per Flight Date:</b> Performs multiple separate per-date ANOVAs for selected flights.{" "}
			<i>Recommended for &lt;3 flights.</i>
		</p>
		<p>
			<b>Repeated Measures:</b> Performs a Repeated Measures ANOVA across selected flights to evaluate treatment
			response over time. <i>Requires &ge;3 flights.</i>
		</p>
	</div>
);
const rcbdWarningInfo =
	"Selected experimental design must be a Randomized Complete Block Design (RCBD) in order to run mean comparison tests";

const DropdownWithLabel = (props: DropdownProps & { label: string; info?: React.ReactNode; pdf?: string }) => {
	return (
		<div>
			<label style={{ display: "flex", gap: 6 }}>
				{props.label}
				{!!props.info && (
					<Popup
						offset={[-11]}
						wide={"very"}
						trigger={<Icon style={{ margin: 0, verticalAlign: "top" }} name="info circle" />}
						on={"hover"}
						content={props.info}
					/>
				)}
				{props.pdf && (
					<a href={!props.disabled ? props.pdf : undefined} target="_blank" rel="noreferrer">
						read more
					</a>
				)}
			</label>
			<Dropdown selection fluid search {...props} />
		</div>
	);
};

const ToggleWithLabel = (props: CheckboxProps & { label: string; info?: string }) => {
	return (
		<div>
			<label htmlFor={props.id as string | undefined} style={{ display: "unset" }}>
				{props.label}
				{!!props.info && (
					<Popup
						offset={[-11]}
						trigger={<Icon style={{ marginLeft: 10, verticalAlign: "top" }} name="info circle" />}
						on={"hover"}
						content={props.info}
					/>
				)}
			</label>
			<Checkbox style={{ height: "38px", display: "flex", alignItems: "center" }} toggle {...props} label={undefined} />
		</div>
	);
};

type Props = {
	options?: TrialAnalysisDataOptions;
	mode?: "timecourse" | "boxWhisker";
};

const AnalysisTopBar = ({ options, mode = "timecourse" }: Props) => {
	const { changeSettings, commitSettings, changeDisplaySettings, changeSettingsSilently } = useTrialAnalysisDispatch();
	const { display, pending, isDirty, actualSelectedFlights } = useTrialAnalysis();

	const [accordionActive, setAccordionActive] = useState(false);
	const [stagedDataType, setSelectedDataType] = useState<string>();

	const timeSeriesPresets = useMemo(() => options && getOrderedTimeSeriesPresets(options), [options]);
	const growthCurveAssessments = useMemo(() => options && getGrowthCurveAssessments(options), [options]);
	const groundDataAssessments = useMemo(() => options && getGroundDataAssessments(options), [options]);
	const allAssessments = useMemo(
		() =>
			timeSeriesPresets &&
			growthCurveAssessments &&
			groundDataAssessments &&
			_.concat<{ name: string; assessmentCategoryId: string; id: string }>(
				timeSeriesPresets,
				growthCurveAssessments,
				groundDataAssessments,
			),
		[timeSeriesPresets, growthCurveAssessments, groundDataAssessments],
	);
	// DDLs
	const dataTypeOptions = useMemo(() => {
		if (!allAssessments || !options) return undefined;

		const opts = _.chain(options.presetOptions.categories)
			.filter((c) => _.some(allAssessments, (ga) => ga.assessmentCategoryId === c.id))
			.orderBy(["order", "name"])
			.map((c) => ({
				key: c.id,
				value: c.id,
				text: c.name,
			}))
			.value();

		return opts;
	}, [allAssessments, options]);

	const selectedDataType = useMemo(
		() =>
			stagedDataType ??
			_.find(options?.presetOptions.categories, { name: "Aerial Time Series" })?.id ??
			dataTypeOptions?.[0]?.value,
		[dataTypeOptions, options?.presetOptions.categories, stagedDataType],
	);

	const currentCategory = useMemo(() => {
		if (!selectedDataType) return undefined;
		const category = _.find(options?.presetOptions.categories, { id: selectedDataType });
		if (!category) return undefined;
		if (TIME_SERIES_CATEGORIES.includes(category.name)) return Category.TimeSeries;
		if (GROWTH_CURVE_CATEGORIES.includes(category.name)) return Category.GrowthCurve;
		if (GROUND_DATA_CATEGORIES.includes(category.name)) return Category.GroundData;
		return Category.GroundData;
	}, [options?.presetOptions.categories, selectedDataType]);

	const assessmentOptions = useMemo(() => {
		if (!timeSeriesPresets || !allAssessments) return undefined;
		let opts = _.map(timeSeriesPresets, (tsp) => ({
			key: tsp.id,
			value: tsp.id,
			text: tsp.name,
		}));

		if (mode === "boxWhisker" && selectedDataType) {
			opts = _.chain(allAssessments)
				.filter((aa) => aa.assessmentCategoryId === selectedDataType)
				.map((a) => ({ key: a.id, value: a.id, text: a.name }))
				.value();
		}

		return opts;
	}, [mode, selectedDataType, timeSeriesPresets, allAssessments]);

	const dateOptions = useMemo(() => {
		if (!options || !currentCategory) return undefined;
		if (currentCategory === Category.TimeSeries) {
			const opts = _.chain(options.trialAnalysisOptions)
				.filter((ta) =>
					_.some(
						ta.analysisTypes,
						(at) =>
							at.id === pending.analysisId &&
							at.analysisTypeId === pending.analysisTypeId &&
							at.orthoImageTypeId === pending.orthoImageTypeId &&
							at.quantifiedRegionTypeId === pending.quantifiedRegionTypeId,
					),
				)
				.orderBy([(ta) => DateTime.fromISO(ta.flightDate).toMillis()])
				.map((ta) => ({
					key: ta.flightId,
					value: ta.flightId,
					text: DateTime.fromISO(ta.flightDate).toLocal().toFormat("LL/dd/yy"),
				}))
				.value();

			return opts;
		} else if (currentCategory === Category.GroundData) {
			const opts = _.chain(options.groundDataOptions)
				.filter((gd) => _.some(gd.groundDataAssessments, (gda) => gda.id === pending.groundDataAssessmentId))
				.orderBy([(gd) => DateTime.fromISO(gd.assessmentDate).toMillis()])
				.map((gd) => ({
					key: gd.groundDatasetId,
					value: gd.groundDatasetId,
					text: DateTime.fromISO(gd.assessmentDate).toLocal().toFormat("LL/dd/yy"),
				}))
				.value();

			return opts;
		}
		return undefined;
	}, [options, currentCategory, pending]);

	const experimentalDesignOptions = useMemo(() => {
		if (!options || options.experimentalDesignFactors.length === 0) return undefined;
		const opts = options.experimentalDesignFactors.map((ed, i) => ({
			key: ed.id,
			text: `EXP${i} - ${ed.name}`,
			value: ed.id,
		}));
		return opts;
	}, [options]);

	const meanComparisonOptions = useMemo(() => {
		if (!options) return undefined;
		const opts = _.map(options.meanComparisonOptions, (mco) => ({
			key: mco.id,
			value: mco.id,
			text: mco.name,
		}));
		return opts;
	}, [options]);

	const anovaTypeOptions = useMemo(
		() => [
			{ key: "one-way", value: "one-way", text: "Per Flight Date" },
			{
				key: "repeated",
				value: "repeated",
				text: "Repeated Measures",
				disabled: (actualSelectedFlights.length ?? 0) < 3,
			},
		],
		[actualSelectedFlights],
	);

	const alphaOptions = useMemo(() => {
		if (!options) return undefined;
		const opts = _.chain(options.alphaOptions)
			.map((ao) => ({
				key: ao.id,
				value: ao.id,
				text: ao.value,
			}))
			.orderBy([(o) => Number(o.text)], "asc")
			.value();
		return opts;
	}, [options]);

	const analysisTypeOptions = useMemo(() => {
		if (!options) return undefined;
		const opts = _.map(options.presetOptions.analysisTypes, (at) => ({
			key: at.id,
			value: at.id,
			text: at.name,
		}));

		if (mode === "timecourse") {
			return _.filter(opts, (o) => o.text !== "Stand Count");
		} else {
			return opts;
		}
	}, [mode, options]);

	const orthoOptions = useMemo(() => {
		if (!options) return undefined;
		const trialAnalyses = _.chain(options.trialAnalysisOptions)
			.flatMap<AnalysisType>("analysisTypes")
			.uniqBy("id")
			.value();
		const opts = _.map(trialAnalyses, (ta) => ({
			key: ta.orthoImageTypeId,
			value: ta.orthoImageTypeId,
			text: ta.displayName,
		}));
		return opts;
	}, [options]);

	const qrTypeOptions = useMemo(() => {
		if (!options) return undefined;
		const opts = _.map(options.quantifiedRegionTypeOptions, (qrto) => ({
			key: qrto.id,
			value: qrto.id,
			text: qrto.name,
		}));
		return opts;
	}, [options]);

	const listOfQrTypeIds = useMemo(() => _.map(options?.quantifiedRegionTypeOptions, (qrt) => qrt.id), [options]);

	const handleAssessmentChange = useCallback(
		(assessmentId: string, assessmentName: string) => {
			if (allAssessments) {
				changeDisplaySettings({ assessmentName: assessmentName });

				const timeSeriesPreset = _.find(timeSeriesPresets, (tsp) => tsp.id === assessmentId);
				if (timeSeriesPreset) {
					changeSettingsSilently({
						assessmentId: assessmentId,
						analysisId: timeSeriesPreset.analysisId,
						analysisTypeId: timeSeriesPreset.analysisTypeId,
						isPlotUniformity: false, // FIXME: this is really bad!
						orthoImageTypeId: timeSeriesPreset.orthoImageTypeId,
						quantifiedRegionTypeId: listOfQrTypeIds.includes(timeSeriesPreset.quantifiedRegionTypeId)
							? timeSeriesPreset.quantifiedRegionTypeId
							: listOfQrTypeIds.includes(timeSeriesPreset.secondaryQuantifiedRegionTypeId)
								? timeSeriesPreset.secondaryQuantifiedRegionTypeId
								: pending.quantifiedRegionTypeId,
						curveModelAnalysisId: null,
						groundDataAssessmentId: null,
					});
					return;
				}
				const growthCurveAssessment = _.find(growthCurveAssessments, (gca) => gca.id === assessmentId);
				if (growthCurveAssessment) {
					changeSettingsSilently({
						assessmentId: assessmentId,
						analysisId: growthCurveAssessment.analysisId,
						analysisTypeId: growthCurveAssessment.analysisTypeId,
						orthoImageTypeId: growthCurveAssessment.orthoImageId,
						quantifiedRegionTypeId: growthCurveAssessment.quantifiedRegionTypeId,
						curveModelAnalysisId: growthCurveAssessment.curveModelAnalysisId,
						groundDataAssessmentId: null,
						flightId: null,
					});
					return;
				}
				const groundDataAssessment = _.find(groundDataAssessments, (gda) => gda.id === assessmentId);
				if (groundDataAssessment) {
					changeSettingsSilently({
						assessmentId: assessmentId,
						groundDataAssessmentId: groundDataAssessment.id,
						analysisId: null,
						analysisTypeId: null,
						orthoImageTypeId: null,
						quantifiedRegionTypeId: null,
						curveModelAnalysisId: null,
					});
					return;
				}
			}
		},
		[
			changeSettingsSilently,
			changeDisplaySettings,
			groundDataAssessments,
			growthCurveAssessments,
			listOfQrTypeIds,
			allAssessments,
			pending.quantifiedRegionTypeId,
			timeSeriesPresets,
		],
	);

	const handleExperimentalDesignChange = useCallback(
		(expId: string) => {
			if (!options) return;
			const exp = _.find(options.experimentalDesignFactors, (exp) => exp.id === expId);
			if (exp) {
				changeDisplaySettings({
					experimentalDesignId: expId,
					treatmentNames: exp.treatmentNames,
					isRCBD: exp.factorType === "RCBD",
				});
			}
		},
		[changeDisplaySettings, options],
	);

	useEffect(() => {
		if (!assessmentOptions) return;
		if (!_.some(assessmentOptions, { key: pending.assessmentId })) {
			handleAssessmentChange(assessmentOptions[0].value, assessmentOptions[0].text);
		}
	}, [assessmentOptions, handleAssessmentChange, pending.assessmentId]);

	useEffect(() => {
		if (
			dateOptions?.length &&
			!_.some(dateOptions, (date) => date.value === pending.flightId || date.value === pending.groundDatasetId)
		) {
			if (currentCategory === Category.TimeSeries) {
				changeSettings({ flightId: dateOptions[0].value, groundDatasetId: null });
			} else if (currentCategory === Category.GroundData) {
				changeSettings({ groundDatasetId: dateOptions[0].value, flightId: null });
			}
		}
	}, [pending, currentCategory, changeSettings, dateOptions]);

	// useEffect(() => {
	// 	if (!isFetchingExpDesigns && options.experimentalDesignFactors.length && !pending.experimentalDesignId) {
	// 		const defaultExp = _.find(expDesignsDropdownInfo, (ed) => ed.default) ?? expDesignsDropdownInfo[0];
	// 		handleExperimentalDesignChange(defaultExp.id);
	// 	}
	// }, [expDesignsDropdownInfo, isFetchingExpDesigns, handleExperimentalDesignChange, pending]);

	return (
		<div className={styles.topBarContainer}>
			<Accordion fluid>
				<Accordion.Title active={accordionActive}>
					<div style={{ display: "flex", gap: "16px", alignItems: "center" }}>
						<div
							onClick={() => {
								setAccordionActive((prev) => !prev);
							}}
							style={{ fontSize: "1.28571429rem", fontWeight: "700", cursor: "pointer" }}
						>
							<Icon name="dropdown" style={{ margin: 0 }} />
						</div>
						{mode === "boxWhisker" && (
							<DropdownWithLabel
								label="Data Type"
								loading={dataTypeOptions === undefined}
								options={dataTypeOptions ?? []}
								value={selectedDataType}
								onChange={(_, { value }) => {
									setSelectedDataType(value as string);
								}}
							/>
						)}
						<DropdownWithLabel
							label="Assessment"
							loading={assessmentOptions === undefined}
							options={assessmentOptions ?? []}
							value={pending.assessmentId ?? undefined}
							onChange={(_, data) => {
								handleAssessmentChange(
									data.value as string,
									data.options!.find((o) => o.key === data.value)?.text as string,
								);
							}}
						/>
						{mode === "boxWhisker" && currentCategory && CATEGORIES_WITH_DATES.includes(currentCategory) && (
							<DropdownWithLabel
								label="Assessment Date"
								loading={dateOptions === undefined}
								options={dateOptions ?? []}
								value={
									currentCategory === Category.TimeSeries
										? (pending.flightId ?? undefined)
										: currentCategory === Category.GroundData
											? (pending.groundDatasetId ?? undefined)
											: undefined
								}
								onChange={(_event, { value }) => {
									if (currentCategory === Category.TimeSeries) {
										changeSettingsSilently({ flightId: value as string, groundDatasetId: null });
									} else if (currentCategory === Category.GroundData) {
										changeSettingsSilently({ groundDatasetId: value as string, flightId: null });
									}
								}}
							/>
						)}
						{!!experimentalDesignOptions && (
							<DropdownWithLabel
								label="Experimental Design"
								loading={experimentalDesignOptions === undefined}
								options={experimentalDesignOptions ?? []}
								value={display.experimentalDesignId ?? undefined}
								onChange={(_event, { value }) => {
									handleExperimentalDesignChange(value as string);
								}}
							/>
						)}
						<Popup
							disabled={display.isRCBD}
							position="top center"
							trigger={
								<div style={{ opacity: !display.isRCBD ? 0.6 : 1, display: "flex", gap: "16px", alignItems: "center" }}>
									<DropdownWithLabel
										label="Mean Comparison"
										loading={meanComparisonOptions === undefined}
										options={meanComparisonOptions ?? []}
										disabled={!display.isRCBD}
										value={pending.meanComparisonOptionId ?? undefined}
										onChange={(_, { value }) => {
											changeSettings({ meanComparisonOptionId: value as string });
										}}
									/>
									{mode === "timecourse" && (
										<DropdownWithLabel
											label="ANOVA Type"
											loading={meanComparisonOptions === undefined}
											options={anovaTypeOptions}
											disabled={!display.isRCBD}
											pdf={anovaPdf}
											info={anovaTypeInfo}
											value={pending.anovaType}
											onChange={(_, { value }) => {
												changeSettings({ anovaType: value as "one-way" | "repeated" });
											}}
										/>
									)}
									<DropdownWithLabel
										label="&alpha; Value"
										loading={alphaOptions === undefined}
										options={alphaOptions}
										disabled={!display.isRCBD}
										value={pending.alphaOptionId ?? undefined}
										onChange={(_, { value }) => {
											changeSettings({ alphaOptionId: value as string });
										}}
									/>
								</div>
							}
							content={rcbdWarningInfo}
						/>
						{mode === "timecourse" && display.hasGdus && (
							<ToggleWithLabel
								id="use-gdus-toggle"
								label="Use GDUs"
								checked={pending.useGdu}
								onChange={(_, { checked }) => {
									changeSettings({ useGdu: checked });
								}}
							/>
						)}
						<ToggleWithLabel
							id="remove-exclusions-toggle"
							label="Remove Exclusions"
							info={removeExclusionsInfo}
							checked={pending.removeDamagedOrExcluded}
							onChange={(_, { checked }) => {
								changeSettings({ removeDamagedOrExcluded: checked });
							}}
						/>
						{mode === "timecourse" && (
							<ToggleWithLabel
								id="collapse-table"
								label="Collapsed View"
								checked={display.collapseTable}
								onChange={(_, { checked }) => {
									changeDisplaySettings({ collapseTable: checked });
								}}
							/>
						)}
						<Button
							style={{ alignSelf: "flex-end" }}
							color={!isDirty ? "blue" : undefined}
							negative={isDirty}
							content="Update Analytics"
							onClick={() => {
								commitSettings();
							}}
						/>
					</div>
				</Accordion.Title>
				<Accordion.Content active={accordionActive} style={{ padding: "0 0 6px 22.5px", marginLeft: "16px" }}>
					<div style={{ display: "flex", gap: "16px" }}>
						<DropdownWithLabel
							label="Analysis Type"
							style={{ width: 125 }}
							loading={analysisTypeOptions === undefined}
							options={analysisTypeOptions}
							value={pending.analysisTypeId ?? undefined}
							onChange={(_event, { value, text }) => {
								if (timeSeriesPresets) {
									const currentPreset = _.find(timeSeriesPresets, (tsp) => tsp.analysisTypeId === value);
									if (currentPreset) {
										changeSettings({
											assessmentId: currentPreset.id,
											analysisId: currentPreset.analysisId,
											analysisTypeId: currentPreset.analysisTypeId,
											isPlotUniformity: text === "Uniformity", // FIXME: this is really bad!
										});
									} else {
										changeSettings({ analysisTypeId: value as string, isPlotUniformity: text === "Uniformity" });
									}
								}
							}}
						/>
						<DropdownWithLabel
							label="Ortho"
							loading={orthoOptions === undefined}
							options={orthoOptions}
							value={pending.orthoImageTypeId ?? undefined}
							onChange={(_event_, { value }) => {
								if (timeSeriesPresets) {
									const currentPreset = _.find(timeSeriesPresets, (tsp) => tsp.orthoImageTypeId === value);
									if (currentPreset) {
										changeSettings({
											assessmentId: currentPreset.id,
											analysisId: currentPreset.analysisId,
											orthoImageTypeId: currentPreset.orthoImageTypeId,
										});
									} else {
										changeSettings({ orthoImageTypeId: value as string });
									}
								}
							}}
						/>
						<DropdownWithLabel
							label="Quantified Region"
							loading={qrTypeOptions === undefined}
							options={qrTypeOptions}
							value={pending.quantifiedRegionTypeId ?? undefined}
							onChange={(_event, { value }) => {
								changeSettings({ quantifiedRegionTypeId: value as string });
							}}
						/>
					</div>
				</Accordion.Content>
			</Accordion>
		</div>
	);
};

export default AnalysisTopBar;
